//Hooks
import { get } from 'svelte/store';
//Formating
import { format } from 'svelte-i18n';
//Helpers
import { convertToUnit, convertToValueAndUnit } from './units_helpers';
import { isDistance, real_id } from './interface_helpers';
import { postReq } from 'src/services/http';
import { devices, devices_settings } from 'src/stores';
//Types
import type { GetStatsResponseData } from 'src/types/requests/charts/get_stats_period';
import type { ChartConfig } from 'src/types/Chart';
import type { Device } from 'src/types/Device';

export const DISTANCE_BASE_SIZE = 118;
export const DISTANCE_MIN_NOTATION = 1e-6;

export const conversionFactors = {
	mm: 1,
	cm: 1,
	in: 25.4, // 1 inch = 25.4 mm
};

export const convertDistanceChartUnits = (
	data: GetStatsResponseData,
	chartConfig: ChartConfig,
): void => {
	const device: Device = get(devices)[chartConfig.deviceID];
	const deviceSettings = get(devices_settings)[real_id(device.id)];
	const unit = device?.ht_ui?.unit || deviceSettings?.ht_ui?.unit || 'mm';
	data.history = data.history.map((i) => {
		if (i?.missing) return i;
		return {
			...i,
			min: adjust(i.min, device?.measureFromBase),
			max: adjust(i.max, device?.measureFromBase),
			avg: adjust(i.avg, device?.measureFromBase),
			value_converted: true,
		};
	});
	chartConfig.units = unit === 'in' ? 'in' : 'mm';
};

export const DISTANCE_ADDITIONAL_PROPS = {
	onboarded: false,
	modeType: 'liquids',
	solidsType: 'Generic',
	garageType: 'Vertical',
	closedToleranceRange: 10,
	closedPositionRange: 300,
	installationHeight: 2000,
	reverseMeasurement: false,
	measureFromBase: false,
	triggerDistance: 1000,
	meterRangeStart: 1650,
	meterRangeEnd: 850,
};

export const deviceSlotsParams = (type: string) => ({
	mainActionType: 'param',
	statusLabelParam: [
		{
			n: 'battery',
			o: 1,
			v: true,
			m: false,
		},
		{
			n: 'vibration',
			o: 2,
			v: true,
			m: false,
		},
		{
			n: 'distance',
			o: 3,
			v: true,
			m: false,
		},
		{
			n: type,
			o: 4,
			v: false,
			m: true,
		},
	],
});

export const calculateWidths = (
	min: number,
	max: number,
	// Note: totalMax and totalMin here are the numerical limits,
	// NOT the visual labels in the component.
	numericalMin: number,
	numericalMax: number,
) => {
	// Clamp the values to valid range
	const clampedMin = Math.max(numericalMin, Math.min(min, numericalMax));
	const clampedMax = Math.max(numericalMin, Math.min(max, numericalMax));

	// Ensure the ordering (in case user swaps inputs)
	const high = Math.max(clampedMin, clampedMax);
	const low = Math.min(clampedMin, clampedMax);

	const totalRange = numericalMax - numericalMin;

	if (totalRange <= 0) {
		return { left: 0, middle: 0, right: 0 };
	}

	const standardLeft = (low - numericalMin) / totalRange; // Space before low
	const standardMiddle = (high - low) / totalRange; // Space between high/low
	const standardRight = (numericalMax - high) / totalRange; // Space after high

	// The visual LEFT gap corresponds to the standard RIGHT proportion (space above 'high').
	const left = standardRight * 100;

	// The MIDDLE filled section remains the same.
	const middle = standardMiddle * 100;

	// The visual RIGHT gap corresponds to the standard LEFT proportion (space below 'low').
	const right = standardLeft * 100;

	// The component expects { left, middle, right } for the chart divs.
	return { left, middle, right };
};

export const repositionChartWidthsLabels = (percent: number) => {
	return Math.min(Math.max(percent, 0), 87);
};

/**
 * Translates distance unit labels using get(format)()
 * @param {string} value - The distance value (1 m, cm, mm)
 * @param {string} deviceType
 * @returns {string} - The translated unit label
 */
export const formatDistanceUnit = (value, deviceType) => {
	if (!value || typeof value !== 'string' || !isDistance(deviceType)) {
		return value;
	}

	let translated = value;
	if (value.endsWith('cm')) {
		translated = value.replace('cm', get(format)('distance_unit_cm', { default: 'cm' }));
	} else if (value.endsWith('mm')) {
		translated = value.replace('mm', get(format)('distance_unit_mm', { default: 'mm' }));
	} else if (value.endsWith('m')) {
		translated = value.replace('m', get(format)('distance_unit_m', { default: 'm' }));
	}

	return translated;
};

/**
 * Helper for the function below
 */
const adjust = (value: number, measureFromBase: boolean) => {
	if (value > 0 && measureFromBase) value += DISTANCE_BASE_SIZE;
	return value;
};

export const convertDistUnits = (unit) => {
	if (['mm', 'cm', 'm'].includes(unit)) {
		return get(format)('distance_unit_mm', { default: 'mm' });
	} else {
		return get(format)(`distance_unit_${unit}`, { default: unit });
	}
};

export const calculateDistValues = (value, unit, end = false) => {
	return `${
		convertToValueAndUnit(value, unit === 'in' ? 'in' : 'mm', {
			fixed: ['m', 'in'].includes(unit) ? 2 : 1,
			dontConvert: ['mm', 'cm'].includes(unit),
		}).value
	}${end && unit !== 'in' ? ` ${convertDistUnits('mm')}` : ''}`;
};

/**
 * Convert and format a distance for display
 * @param {string | number} value - The distance value
 * @param {string} unit - The distance unit
 * @param {string} deviceType
 * @returns {string} - The translated unit label
 */
export const convertAndFormatDistance = (value, unit, deviceType) => {
	const fixed = ['m', 'in'].includes(unit) ? 2 : 1;
	const dontConvert = unit === 'mm';

	return formatDistanceUnit(
		convertToUnit(value, unit === 'in' ? 'in' : 'mm', {
			fixed,
			dontConvert,
		}),
		deviceType,
	);
};

export const updateDeviceDistanceSettings = (device) => {
	return postReq('interface/device/set_dst', {
		id: device.id,
		dst: JSON.stringify({
			installationHeight: device.installationHeight,
			measureFromBase: device.measureFromBase,
			triggerDistance: device.triggerDistance,
			meterRangeStart: device.meterRangeStart,
			meterRangeEnd: device.meterRangeEnd,
		}),
	});
};

export const saveDevice = (device) => {
	return postReq('interface/device/save', {
		id: device.id,
		data: JSON.stringify(device),
	});
};

// -----------------------------------------------
// Master garage config (single source of truth)
// -----------------------------------------------
const garageConfig = {
	'Horizontal (left to right)': {
		opening: {
			image: 'door_horizontal_left_opening.png',
			icon: 'fas fa-arrow-left',
		},
		closing: {
			image: 'door_horizontal_left_closing.png',
			icon: 'fas fa-arrow-right',
		},
		open: {
			image: 'door_horizontal_left_open.png',
			icon: 'fas fa-garage-empty',
		},
		closed: {
			image: 'door_horizontal_left_closed.png',
			icon: 'fas fa-garage',
		},
	},

	'Horizontal (right to left)': {
		opening: {
			image: 'door_horizontal_right_opening.png',
			icon: 'fas fa-arrow-right',
		},
		closing: {
			image: 'door_horizontal_right_closing.png',
			icon: 'fas fa-arrow-left',
		},
		open: {
			image: 'door_horizontal_right_open.png',
			icon: 'fas fa-garage-empty',
		},
		closed: {
			image: 'door_horizontal_right_closed.png',
			icon: 'fas fa-garage',
		},
	},

	default: {
		opening: {
			image: 'door_vertical_opening.png',
			icon: 'fas fa-arrow-up',
		},
		closing: {
			image: 'door_vertical_closing.png',
			icon: 'fas fa-arrow-down',
		},
		open: {
			image: 'door_vertical_open.png',
			icon: 'fas fa-garage-empty',
		},
		closed: {
			image: 'door_vertical_closed.png',
			icon: 'fas fa-garage',
		},
	},
};

// -----------------------------------------------
// Single accessor (used by all specialized helpers)
// -----------------------------------------------
const getGarageStateConfig = (garageType, action) => {
	const typeConfig = garageConfig[garageType] || garageConfig.default;
	return typeConfig[action];
};

// -----------------------------------------------
// Public helpers
// -----------------------------------------------
export const getGarageImageUrlSuffix = (garageType, action) => {
	return getGarageStateConfig(garageType, action).image;
};

export const getGarageIconStatus = (garageType, action) => {
	return getGarageStateConfig(garageType, action).icon;
};

// module scope (top of device_data_helpers.ts)
type GarageState = {
	stableDistance: number | null; // baseline used for comparisons
	lastDirection: 'opening' | 'closing' | null;
	confirmCount: number; // how many consecutive confirming readings we've seen
	confirmNeeded: number; // how many confirms required to actually move the stableDistance
	lastUpdateTs: number | null; // optional time-lock
};

export const garageStates = new Map<string | number, GarageState>();
